// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import Notify from "@adl/App/Components/Notify/Notify";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getProductSpecialDiscontinuedItem } from "@adl/App/Store/Selectors/ProductDetailsSelector";
import { hideElementOnPrint, xScreen } from "@adl/XDL/Utilities";
import color from "@adl/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, useShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasShellContext & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    specialDiscontinuedItem: getProductSpecialDiscontinuedItem(state, ownProps.product),
});

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    leftSection?: InjectableCss;
    mainContent?: InjectableCss;
    rightSection?: InjectableCss;
    imagesSection?: InjectableCss;
    detailsSection?: InjectableCss;
    actionsWrapper?: InjectableCss;
    detailsAndSpecificationsSection?: InjectableCss;
    frequentlyBoughtTogetherSection?: InjectableCss;
    stickyWrapper?: InjectableCss;
    detailsSectionTablet?: InjectableCss;
    tabletDetailsSection?: InjectableCss;
}

const ProductPreviousPurchaseStyle = `
    position: absolute;
    right: 0;
    bottom: 30px;
`;

export const viewStyles: ProductDetailsViewStyles = {
    container: {
        css: css`
            display: flex;
            margin-bottom: 62px;
            ${xScreen("lte", "md")} {
                justify-content: space-between;
                margin-bottom: 20px;
                flex-wrap: wrap;
                & > div:first-child {
                    width: 100%;
                }
            }
            span[data-test-selector="ProductPreviousPurchase"] {
                ${ProductPreviousPurchaseStyle}
                ${xScreen("lte", "md")} {
                    display: none;
                }
            }
        `,
    },
    leftSection: {
        css: css`
            flex: 0 1 968px;
            margin-right: 22px;
            ${xScreen("lte", "md")} {
                flex: 0 0 352px;
                max-width: 352px;
                margin-right: 0;
            }
            ${xScreen("lte", "725")} {
                flex: 0 0 100%;
                max-width: 100%;
                margin-right: 0;
                margin-bottom: 18px;
                overflow: hidden;
                flex-direction: column-reverse;
            }
        `,
    },
    mainContent: {
        css: css`
            display: flex;
            ${xScreen("lte", "md")} {
                margin: 0;
                max-width: 100%;
                flex-wrap: wrap;
            }
        `,
    },
    imagesSection: {
        css: css`
            flex: 0 0 308px;
            margin-right: 22px;
            ${xScreen("lte", "md")} {
                & > div > div:last-child {
                    margin-top: 16px;
                }
            }
            ${xScreen("lte", "sm")} {
                flex: 0 0 100%;
                flex-direction: column-reverse;
                max-width: 100%;
                margin-right: 0;
            }
        `,
    },
    detailsSection: {
        css: css`
            flex: 0 1 638px;
            > div {
                position: relative;
            }
            ${xScreen("lte", "md")} {
                display: none;
                span[data-test-selector="ProductPreviousPurchase"] {
                    display: none;
                }
            }
        `,
    },
    rightSection: {
        css: css`
            flex: 0 0 308px;
            ${xScreen("lte", "md")} {
                div[class^="InventoryMessage"] {
                    margin-top: 16px;
                }
                > div div:not([class^="TooltipContainer"]) {
                    padding: 0 !important;
                }
            }
            ${xScreen("lte", "725")} {
                flex: 0 0 100%;
                max-width: 100%;
                div[class^="InventoryMessage"] {
                    margin-top: 0;
                }
                > div > div {
                    justify-content: space-between;
                }
            }
        `,
    },
    actionsWrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 2px;
            background: ${color("common.white")};
            padding: 20px 16px 16px;
            @media print {
                break-inside: avoid;
            }
            ${xScreen("lte", "md")} {
                padding: 18px;
            }
        `,
    },
    detailsAndSpecificationsSection: {
        css: css`
            flex: 0 0 100%;
        `,
    },
    frequentlyBoughtTogetherSection: {
        css: css`
            flex: 0 0 100%;
        `,
    },
    stickyWrapper: {
        css: css`
            position: fixed;
            left: 0;
            background: ${color("common.white")};
            width: 100%;
            z-index: 1300;
            transition: all 300ms ease-out;
            opacity: 1;
            pointer-events: initial;
            min-height: 94px;
            height: auto;
            box-shadow: 0 0 1px 0 ${color("common.ui8", 0.25)};

            &.stickyVisible {
                top: 0;
            }

            &.stickyHidden {
                top: -250px;
                &,
                & > * {
                    max-height: 0;
                    overflow: hidden;
                }
            }

            &.editingMode {
                position: unset;
            }

            ${xScreen("lte", "md")} {
                display: none;
            }

            ${hideElementOnPrint}
        `,
    },
    detailsSectionTablet: {
        css: css`
            & > div {
                & > p,
                & > div {
                    display: none;
                }
            }
            margin-bottom: 24px;
            ${xScreen("gt", "md")} {
                display: none;
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 32px;
            }
        `,
    },
    tabletDetailsSection: {
        css: css`
            ${xScreen("gte", "lg")} {
                display: none;
            }
            position: relative;
            h1[data-test-selector="productDetails_productDescription"] {
                display: none;
            }
            span[data-test-selector="ProductPreviousPurchase"] {
                ${ProductPreviousPurchaseStyle}
            }
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<Props> = props => {
    const [stickyVisible, setStickyVisible] = useState(false);
    const { isEditing } = useShellContext();
    const isShellEditingMood = props.shellContext?.isInShell && props.shellContext?.isEditing;

    let descElement: any = null;
    const handleSticky = () => {
        const isSticky = (isSticky: boolean) => {
            setStickyVisible(isSticky);
        };

        if (!descElement) {
            descElement = document.querySelector('[data-test-selector="productDetails_bottomSection"]');
            isSticky(false);
            return;
        }

        if (window.scrollY > descElement?.offsetTop - 150) {
            isSticky(true);
        } else {
            isSticky(false);
        }
    };

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, []);

    return (
        <>
            {props.specialDiscontinuedItem === "true" && (
                <Notify type="warning">{siteMessage("Custom_ProductDetails_SpecialDiscontinuedItemMessage")}</Notify>
            )}
            <StyledWrapper {...styles.container} data-test-selector="productDetailsView">
                {!isEditing && (
                    <StyledWrapper {...styles.detailsSectionTablet}>
                        <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Title" />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper {...styles.mainContent}>
                        <StyledWrapper id="Content0" {...styles.imagesSection}>
                            <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                        </StyledWrapper>
                        <StyledWrapper id="Content1" {...styles.detailsSection}>
                            <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection} data-test-selector="productDetails_rightColumn">
                    <StyledWrapper {...styles.actionsWrapper}>
                        <XngZone contentId={props.id} zoneName="Content3" hint="Zone For Summary & Actions" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {!isEditing && (
                <StyledWrapper id="Content3" {...styles.tabletDetailsSection}>
                    <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Product Details" />
                </StyledWrapper>
            )}
            <StyledWrapper data-test-selector="productDetails_bottomSection">
                <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Specifications" />
            </StyledWrapper>
            <StyledWrapper
                className={`${stickyVisible ? "stickyVisible" : "stickyHidden"} ${
                    isShellEditingMood ? "editingMode" : ""
                }`.trim()}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyWrapper}
            >
                <Page background="common.white">
                    <XngZone contentId={props.id} zoneName="StickyHeader" hint="Zone For ProductDetailsHeaderContent" />
                </Page>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(withIsInShell(ProductDetailsView))),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
